import { useQuery as useTanstackQuery } from '@tanstack/react-query';

interface UseQueryProps {
  queryFn: (_: unknown) => void;
  queryData?: unknown;
  onSuccess?: TypeAny;
  onError?: TypeAny;
  enabled?: boolean;
  staleTime?: number; // Cache duration in milliseconds
  refetchOnMount?: boolean; // Whether to refetch on component mount
  queryKey: string;
}

export function useQuery({
  enabled = false,
  onSuccess,
  queryData,
  queryKey,
  queryFn,
  onError,
  staleTime,
  refetchOnMount
}: UseQueryProps) {
  const { isInitialLoading, refetch, isRefetching, error, data } = useTanstackQuery({
    queryFn: async () => queryFn(queryData),
    queryKey: [queryKey, queryData],
    refetchOnWindowFocus: false,
    onSuccess,
    retry: 1,
    onError,
    enabled,
    staleTime,
    refetchOnMount
  });

  // Filter out AbortErrors as they're expected behavior when requests are cancelled
  // Default to returning error unless explicitly filtering out AbortErrors for safety
  const filteredError =
    error && (error as Error)?.name === 'AbortError' ? undefined : (error as ResponseObject);

  return {
    error: filteredError,
    isLoading: isInitialLoading,
    refetch: refetch as TypeAny,
    isRefetching,
    data: data as TypeAny
  };
}
